import { Card, Dialog, Grid } from "@mui/material";
import { useContext } from "react";
import { UsinasContext } from "../context/UsinasContextProvider";
import { PlantString, Producer, UG } from "../types";
import RopTreeChart, {
  RopTreeChartData,
} from "../../../main/components/RopTreeChart";

interface ComponentsChartProps {
  onClose: () => void;
}

export interface SimpleDialogProps {
  open: boolean;
  selectedValue: string;
  onClose: (value: string) => void;
}

const cardStyle: React.CSSProperties = {
  boxShadow:
    "0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)",
  overflow: "hidden",
  borderRadius: "20px",
  padding: "8px 16px 8px 16px",
  margin: "8px 8px 8px 8px",
  width: "98%",
  minHeight: "500px",
};

const ComponentsChart: React.FC<ComponentsChartProps & { open: boolean }> = ({
  onClose,
  open,
}) => {
  const { usina } = useContext(UsinasContext);
  const ugs = usina?.uGs;

  const componentsChartData = componentsToTreeChartData(ugs);
  const invertersAmount = ugs?.reduce((acc, curr) => {
    if (curr.components?.string?.amount) {
      return acc + curr.components.string.amount;
    }
    return acc;
  }, 0);
  const stringsAmount = ugs?.reduce((acc, curr) => {
    if (curr.components?.string?.amount && curr.components?.string?.amount) {
      return (
        acc + curr.components.string.amount * curr.components.string.amount
      );
    }
    return acc;
  }, 0);
  const trackersAmount = ugs?.reduce((acc, curr) => {
    if (curr.components?.trackers?.amount) {
      return acc + curr.components.trackers.amount;
    }
    return acc;
  }, 0);
  const modulesAmount = ugs?.reduce((acc, curr) => {
    if (curr.components?.modules?.amount) {
      return acc + curr.components.modules.amount;
    }
    return acc;
  }, 0);

  const headerItems = [
    {
      name: "Inversores",
      value: invertersAmount,
    },
    {
      name: "Strings",
      value: stringsAmount,
    },
    {
      name: "Trackers",
      value: trackersAmount,
    },
    {
      name: "Módulos",
      value: modulesAmount,
    },
  ];

  const handleClose = () => {
    onClose();
  };

  return (
    <>
      <Grid container>
        <Dialog
          onClose={handleClose}
          open={open}
          PaperProps={{ style: { maxWidth: "none", width: "80%" } }}
        >
          <Card
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              maxWidth: "none",
              ...cardStyle,
            }}
          >
            <RopTreeChart
              title="Componentes"
              data={componentsChartData}
              headerItems={headerItems}
            />
          </Card>
        </Dialog>
      </Grid>
    </>
  );
};

export function componentsToTreeChartData(
  ugs: UG[] | undefined
): RopTreeChartData {
  const plantPower = ugs?.reduce((acc, curr) => {
    if (curr.components?.string?.power && curr.components.string.amount) {
      return acc + curr.components.string.power * curr.components.string.amount;
    }
    return acc;
  }, 0);

  const componentsChartData: RopTreeChartData = {
    name: "Planta",
    children: getPlantTreeChartData(ugs),
    attributes: [
      {
        name: "Potência",
        value: plantPower,
      },
    ],
  };
  return componentsChartData;
}
export function getComponentsTreeChartChildren(
  ugs: UG[] | undefined
): RopTreeChartData[] {
  const children: RopTreeChartData[] = [];

  if (ugs) {
    children.push({
      name: "UGs",
    });
  }
  return children;
}
export function getProducersTooltipItems(producers: Producer[] | undefined): {
  name: string;
  value: string | number | undefined;
}[] {
  const tooltipItems: {
    name: string;
    value: string | number | undefined;
  }[][] = [];
  if (producers) {
    producers.forEach((producer, index) => {
      tooltipItems.push([
        {
          name: `Produtor ${index + 1}`,
          value: undefined,
        },
        {
          name: "Email",
          value: producer.contactEmail,
        },
        {
          name: "Telefone",
          value: producer.contactPhone,
        },
        {
          name: "Portal",
          value: producer.portalAccessLink,
        },
        {
          name: "Usuário",
          value: producer.portalAccessUsername,
        },
        {
          name: "Senha",
          value: producer.portalAccessPassword,
        },
      ]);
    });
  }
  return tooltipItems.flat();
}
export function getPlantTreeChartData(
  ugs: UG[] | undefined
): RopTreeChartData[] {
  const children: RopTreeChartData[] = [];
  if (ugs) {
    ugs.forEach((ug, index) => {
      const ugPower =
        ug.components?.string?.power && ug.components.string?.amount
          ? ug.components?.string?.power * ug.components.string?.amount
          : undefined;
      children.push({
        name: `UG ${index + 1}`,
        attributes: [
          {
            name: "Potência",
            value: ugPower,
          },
        ],
        children: getUGTreeChartData(ug),
      });
    });
  }
  return children;
}
export function getUGTreeChartData(ug: UG | undefined): RopTreeChartData[] {
  const children: RopTreeChartData[] = [];
  if (ug) {
    if (ug.components?.string) {
      if (ug.components.string && ug.components.string.amount) {
        for (let i = 0; i < ug.components.string.amount; i++) {
          children.push({
            name: `Inversor ${i + 1}`,
            attributes: [
              {
                name: "Potência",
                value: ug.components.string.power,
              },
            ],
            children: getInvertersChildrenData(ug.components.string),
          });
        }
      }
    }
  }
  return children;
}

export function getInvertersChildrenData(
  string: PlantString | undefined
): RopTreeChartData[] {
  const children: RopTreeChartData[] = [];
  if (string && string.amount && string.power) {
    for (let i = 0; i < string.amount; i++) {
      children.push({
        name: `String ${i + 1}`,
        attributes: [
          {
            name: "Potência",
            value: string.power,
          },
        ],
      });
    }
  }
  return children;
}
export default ComponentsChart;
